/**
 * Extracts a user-facing message from a parsed API/gateway error object.
 */
function extractMessageFromParsedObject(
  record: Record<string, unknown>
): string | null {
  if (typeof record['message'] === 'string' && record['message']) {
    return record['message'];
  }

  if (typeof record['errorMessage'] === 'string' && record['errorMessage']) {
    return formatApiErrorMessage(record['errorMessage']);
  }

  if (typeof record['description'] === 'string' && record['description']) {
    return record['description'];
  }

  const nestedError = record['error'];
  if (typeof nestedError === 'object' && nestedError !== null) {
    const nested = nestedError as Record<string, unknown>;
    if (typeof nested['description'] === 'string' && nested['description']) {
      return nested['description'];
    }
    if (typeof nested['message'] === 'string' && nested['message']) {
      return nested['message'];
    }
    if (typeof nested['reason'] === 'string' && nested['reason']) {
      return nested['reason'];
    }
  }

  return null;
}

/**
 * Normalizes a single API errorMessage value.
 * Handles JSON-encoded strings such as:
 * {"type":"invalid_request_error","code":"...","message":"Firebase messaging token not found"}
 * {"error":{"code":"BAD_REQUEST_ERROR","description":"Your payment was not successful..."}}
 */
export function formatApiErrorMessage(errorMessage?: string): string | null {
  if (!errorMessage?.trim()) {
    return null;
  }

  const trimmed = errorMessage.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return errorMessage;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (typeof parsed === 'object' && parsed !== null) {
      const extracted = extractMessageFromParsedObject(
        parsed as Record<string, unknown>
      );
      if (extracted) {
        return extracted;
      }
    }
  } catch {
    // Fall back to the original message when JSON parsing fails.
  }

  return errorMessage;
}
